import React, { useEffect, useRef, useState } from 'react';
import { getLogs, clearLog } from '../../services/LogsService';
import { confirmDialog } from '../../utils/ConfirmDialog';

// Strip ANSI escape codes written by Winston colorize
// eslint-disable-next-line no-control-regex
const ANSI_RE = /\x1b\[[0-9;]*m/g;

function parseLine(raw) {
    const line = raw.replace(ANSI_RE, '').trimEnd();
    if (!line) return null;
    const match = line.match(/^(error|warn|info|debug|verbose|silly):\s*/i);
    const level = match ? match[1].toLowerCase() : 'info';
    const text = match ? line.slice(match[0].length) : line;
    return { level, text, raw: line };
}

function levelClass(level) {
    if (level === 'error') return 'text-danger';
    if (level === 'warn')  return 'text-warning';
    if (level === 'debug') return 'text-secondary';
    return 'text-light';
}

const LEVELS = ['all', 'info', 'warn', 'error'];

/**
 * props:
 * - file
 * - logName
 */
const MONITOR_INTERVAL_MS = 3000;

function LogView({ file, logName }) {
    const [lines, setLines]       = useState([]);
    const [levelFilter, setLevel] = useState('all');
    const [search, setSearch]     = useState('');
    const [autoScroll, setAutoScroll] = useState(true);
    const [copyLabel, setCopyLabel]   = useState('Copiar');
    const [monitoring, setMonitoring] = useState(false);
    const bottomRef = useRef(null);

    function fetchLog() {
        if (!file) return;
        getLogs(file)
            .then(raw => {
                const parsed = String(raw).split('\n').map(parseLine).filter(Boolean);
                setLines(parsed);
            })
            .catch(err => {
                const is404 = err.response && err.response.status === 404;
                const msg = is404
                    ? 'Arquivo de log não encontrado. Habilite "Logs" no monitor ou automação para começar a registrar.'
                    : (err.response ? err.response.data : err.message);
                const level = is404 ? 'warn' : 'error';
                setLines([{ level, text: String(msg), raw: String(msg) }]);
            });
    }

    useEffect(() => {
        fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    useEffect(() => {
        if (!monitoring) return;
        const id = setInterval(fetchLog, MONITOR_INTERVAL_MS);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monitoring, file]);

    useEffect(() => {
        if (autoScroll && bottomRef.current)
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [lines, autoScroll]);

    const visible = lines.filter(l => {
        if (levelFilter !== 'all' && l.level !== levelFilter) return false;
        if (search && !l.raw.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    function buildLogText() {
        const title = logName ? `=== Logs — ${logName} ===` : '=== Logs ===';
        const date = new Date().toLocaleString('pt-BR');
        const body = visible.map(l => l.raw).join('\n');
        return `${title}\n${date}\n\n${body}`;
    }

    function handleCopy() {
        navigator.clipboard.writeText(buildLogText()).then(() => {
            setCopyLabel('Copiado!');
            setTimeout(() => setCopyLabel('Copiar'), 2000);
        });
    }

    function handleDownload() {
        const text = buildLogText();
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${logName || file || 'log'}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    }

    async function handleClear() {
        const ok = await confirmDialog({
            title: 'Limpar log',
            message: `Limpar o arquivo de log "${logName || file}"?\n\nEsta ação não pode ser desfeita.`,
            confirmLabel: 'Limpar',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        clearLog(file)
            .then(() => setLines([]))
            .catch(err => alert('Erro ao limpar log: ' + (err.response?.data || err.message)));
    }

    return (
        <div>
            <div className='d-flex gap-2 align-items-center mb-2 flex-wrap'>
                <div className='btn-group btn-group-sm'>
                    {LEVELS.map(lv => (
                        <button
                            key={lv}
                            className={`btn btn-outline-secondary${levelFilter === lv ? ' active' : ''}`}
                            onClick={() => setLevel(lv)}
                        >
                            {lv === 'all' ? 'Todos' : lv.toUpperCase()}
                        </button>
                    ))}
                </div>
                <input
                    type='text'
                    className='form-control form-control-sm'
                    style={{ maxWidth: 220 }}
                    placeholder='Buscar...'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <span className='text-muted small ms-auto'>{visible.length} linha(s)</span>
                <div className='form-check mb-0'>
                    <input
                        className='form-check-input'
                        type='checkbox'
                        id='autoScrollAdmin'
                        checked={autoScroll}
                        onChange={e => setAutoScroll(e.target.checked)}
                    />
                    <label className='form-check-label small' htmlFor='autoScrollAdmin'>Auto-scroll</label>
                </div>
                <button
                    className={`btn btn-sm ${monitoring ? 'btn-success' : 'btn-outline-secondary'}`}
                    title={monitoring ? 'Parar monitoramento' : 'Monitorar (atualiza a cada 3s)'}
                    onClick={() => { setMonitoring(m => !m); if (!monitoring) setAutoScroll(true); }}
                >
                    {monitoring ? '⏹ Monitorando' : '▶ Monitorar'}
                </button>
                <button className='btn btn-sm btn-outline-light' title='Copiar log' onClick={handleCopy}>
                    {copyLabel}
                </button>
                <button className='btn btn-sm btn-outline-light' title='Baixar log' onClick={handleDownload}>
                    Baixar
                </button>
                <button className='btn btn-sm btn-outline-danger' title='Limpar log no servidor' onClick={handleClear}>
                    Limpar
                </button>
            </div>

            <div
                className='bg-dark rounded p-2'
                style={{ height: 420, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12 }}
            >
                {visible.length === 0 && (
                    <span className='text-muted'>Nenhuma linha encontrada.</span>
                )}
                {visible.map((l, i) => (
                    <div key={i} className={levelClass(l.level)}>
                        {l.text}
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>
        </div>
    );
}

export default LogView;
